import React, { useCallback, useEffect, useState } from 'react';
import { ClipLoader } from 'react-spinners';
import useApi from '../../../../../utils/AxiosWrapper';
import { useScreenView } from '../../../../../utils/ScreenViewContext';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useUser } from '../../../../../utils/UserContext';
import { SubmitMileageDriversTable } from './widgets/asset-details/SubmitMileageDriversTable';
import { IOdometerHistory } from './OdometerHistory';
import { MileageReportRequestUpdate } from './SubmitMileage';
import { useForm } from 'react-hook-form';
import { HttpStatusCode } from 'axios';
import toast from 'react-hot-toast';
import { AccentButton } from '../../../../../shared/Buttons';
import {
  CatenaMileageNotice,
  handleMileageConflictResponse,
  readCatenaConnected,
} from './catenaMileage';

interface SubmitMileageDriversProps {
  userType: string;
  currentPage: string;
  currentTab: string;
  setCurrentTab: (tab: string) => void;
  subTab: string;
  setSubTab: (tab: string) => void;
}

export interface ISubmitMileageDriversResponse {
  mileageReportRequests: IOdometerHistory[];
  pagination?: {
    totalCount: number;
    hasMore: boolean;
  };
  /** Customer-level flag: mileage is synced via Catena ELD (THT-678) */
  catena_connected?: boolean;
}

export const SubmitMileageDrivers = ({
  userType,
  currentPage,
  currentTab,
  setCurrentTab,
  subTab,
  setSubTab,
}: SubmitMileageDriversProps) => {
  const {
    register,
    watch,
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<{ assets: MileageReportRequestUpdate[] }>();
  const { screenView } = useScreenView();
  const { callProtectedApi } = useApi();
  const [loading, setLoading] = useState(false);
  const [editingRows, setEditingRows] = useState<Set<string>>(new Set());

  // Toggle editing mode for a specific truck
  const toggleEditing = useCallback((truckId: string) => {
    setEditingRows((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(truckId)) {
        newSet.delete(truckId);
      } else {
        newSet.add(truckId);
      }
      return newSet;
    });
  }, []);

  // Clear editing state for specific truck IDs (called on successful submission)
  const clearEditingForTrucks = useCallback((truckIds: string[]) => {
    setEditingRows((prev) => {
      const newSet = new Set(prev);
      truckIds.forEach((id) => newSet.delete(id));
      return newSet;
    });
  }, []);

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [perPage, setPerPage] = useState(20);
  const queryClient = useQueryClient();

  const { userId, userRecord } = useUser();
  const watchedAssets = watch('assets') || [];

  const isAnyMileageFilled = watchedAssets.some(
    (a) =>
      typeof a?.cumulative_odometer === 'number' && a.cumulative_odometer > 0
  );

  const updateMileageReport = async (updates: MileageReportRequestUpdate[]) => {
    setLoading(true);

    try {
      const response = await callProtectedApi({
        url: `/electronicLoggingDevices/reports/${userRecord?.userType}/${userId}`,
        requestType: 'PATCH',
        body: { updates },
        errorMsg: 'Error occurred while updating mileage report requests.',
        // A rejected reading (below the previous one, or an implausible jump) is only
        // fixable if the driver sees why — the API's 400 says which and by how much.
        preferServerErrorMessage: true,
      });
      if (response?.status === HttpStatusCode.Ok) {
        toast.success('Mileage Successfully Updated');
        // Clear editing state for successfully submitted trucks
        const submittedTruckIds = updates.map((u) => u.truck_id);
        clearEditingForTrucks(submittedTruckIds);
        // Invalidate both mileage report requests and odometer history queries
        queryClient.invalidateQueries({ queryKey: ['mileageReportRequests'] });
        queryClient.invalidateQueries({ queryKey: ['odometerHistory'] });
      } else {
        handleMileageConflictResponse(response, queryClient);
      }
    } catch (e: any) {
      console.error(
        'Error occurred while updating mileage report requests: ',
        e.response
      );
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = (data: { assets: MileageReportRequestUpdate[] }) => {
    const filteredAssets = data.assets.filter(
      (item) =>
        typeof item.cumulative_odometer === 'number' &&
        !isNaN(item.cumulative_odometer) &&
        item.cumulative_odometer > 0
    );

    updateMileageReport(filteredAssets);
  };

  useEffect(() => {
    setPage(1);
    setPageSize(20);
    setPerPage(20);
  }, []);

  const normalizeMileageResponse = (
    raw: any
  ): { items: IOdometerHistory[]; total: number; catenaConnected: boolean } => {
    // Prefer the new explicit contract if present
    const explicit: ISubmitMileageDriversResponse | undefined =
      raw?.mileageReportRequests
        ? raw
        : raw?.data?.mileageReportRequests
          ? raw.data
          : undefined;

    if (explicit) {
      const items = explicit.mileageReportRequests ?? [];
      const total = explicit.pagination?.totalCount ?? items.length;
      return { items, total, catenaConnected: readCatenaConnected(explicit) };
    }

    // Fallbacks (some backends keep nesting)
    const itemsFromCommonNesting =
      raw?.data?.data?.mileageReportRequests ??
      raw?.data?.mileageReportRequests ??
      raw?.mileageReportRequests ??
      [];

    // If customer and backend returns a paginated envelope but different nesting
    const totalGuess =
      raw?.data?.data?.pagination?.totalCount ??
      raw?.pagination?.totalCount ??
      itemsFromCommonNesting.length;

    return {
      items: itemsFromCommonNesting,
      total: totalGuess,
      catenaConnected: readCatenaConnected(raw),
    };
  };

  const fetchSubmitMileageDrivers = useCallback(async () => {
    const response = await callProtectedApi(
      `/electronicLoggingDevices/reports/${userRecord?.userType}/${userId}?status=pending&page=${page}&pageSize=${pageSize}`,
      'get',
      {}
    );

    const raw = response?.data?.data ?? response?.data ?? response;
    return normalizeMileageResponse(raw);
  }, [userId, userRecord?.userType, page, pageSize, callProtectedApi]);

  const {
    data: mileageReportRequestsQueryData,
    isLoading: loadingMileageReports,
  } = useQuery({
    queryKey: ['mileageReportRequests', userId, page, pageSize],
    queryFn: fetchSubmitMileageDrivers,
    enabled: !!userId,
    staleTime: 15 * 60 * 1000,
    gcTime: 15 * 60 * 1000,
  });

  // Handlers
  const handlePerPageChange = (value: number) => {
    setPerPage(value);
    setPage(1);
    setPageSize(value);
  };

  const handlePageChange = (nextPage: number, nextPageSize: number) => {
    if (nextPageSize !== pageSize) setPageSize(nextPageSize);
    setPage(nextPage);
  };

  const mileageReportRequests = mileageReportRequestsQueryData?.items ?? [];
  const mileageReportRequestsCount = mileageReportRequestsQueryData?.total ?? 0;
  const catenaConnected =
    mileageReportRequestsQueryData?.catenaConnected ?? false;

  const FooterButtons = useCallback(() => {
    return (
      <div className="flex h-[80px] max-h-[80px] min-h-[80px] w-full flex-row items-center justify-end gap-x-[16px] border-t-[1px] border-[#EAECF0] align-middle">
        <AccentButton
          addClass={`h-[48px] w-[250px] rounded-full`}
          disabled={loading || !isAnyMileageFilled || catenaConnected}
          submit={true}
        >
          {!loading ? (
            'Save Mileage'
          ) : (
            <ClipLoader
              color="#1D213B"
              loading={loading}
              size={20}
              aria-label="Loading Spinner"
              data-testid="loader"
              className="flex h-full w-full justify-center"
            />
          )}
        </AccentButton>
      </div>
    );
  }, [isAnyMileageFilled, loading, catenaConnected]);

  const renderScreenView = (screenView: string) => {
    switch (screenView) {
      case 'desktop':
      case 'tablet':
      case 'mobile':
        return (
          <form
            onSubmit={handleSubmit(onSubmit)}
            className="flex h-full w-full max-w-full flex-col space-y-4 overflow-x-hidden py-[16px] max-lg:px-4 lg:w-[1148px] lg:max-w-[1148px] lg:pt-[16px]"
          >
            {catenaConnected && !loadingMileageReports && (
              <CatenaMileageNotice />
            )}
            {!loadingMileageReports ? (
              <SubmitMileageDriversTable
                mileageReportRequests={mileageReportRequests}
                catenaConnected={catenaConnected}
                register={register}
                control={control}
                offset={(page - 1) * pageSize}
                limit={pageSize}
                perPage={perPage}
                mileageReportRequestsCount={mileageReportRequestsCount}
                handlePerPageChange={handlePerPageChange}
                handlePageChange={handlePageChange}
                errors={errors}
                editingRows={editingRows}
                onToggleEditing={toggleEditing}
              />
            ) : (
              <ClipLoader
                color="#1D213B"
                loading={loadingMileageReports}
                size={50}
                aria-label="Loading Spinner"
                data-testid="loader"
                className="mt-10 flex h-full w-full items-center justify-center"
              />
            )}
            <FooterButtons />
          </form>
        );
    }
  };

  return <>{renderScreenView(screenView)}</>;
};

export default SubmitMileageDrivers;
